import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShoppingCart, Receipt, FileText, Pencil, Trash2, Eye, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "gm-badge-draft",
  submitted: "gm-badge-submitted",
  approved: "gm-badge-approved",
  rejected: "gm-badge-rejected",
  voided: "bg-gray-200 text-gray-600",
};

export default function MySubmissionsPage() {
  const { activeStoreId, user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || "purchases");

  const load = (preserveTab) => {
    const params = activeStoreId ? `?store_id=${activeStoreId}` : "";
    Promise.all([api.get(`/purchases${params}`), api.get(`/expenses${params}`)])
      .then(([p, e]) => { setPurchases(p.data); setExpenses(e.data); })
      .catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    const params = activeStoreId ? `?store_id=${activeStoreId}` : "";
    Promise.all([api.get(`/purchases${params}`), api.get(`/expenses${params}`)])
      .then(([p, e]) => { setPurchases(p.data); setExpenses(e.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeStoreId]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/${deleteTarget.type}/${deleteTarget.id}`);
      toast.success("Draft discarded");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  }

  const isOwn = (item) => item.submitted_by === user?.id;
  const canEdit = (item) => (item.status === "draft" || item.status === "rejected") && isOwn(item);
  const canDelete = (item) => item.status === "draft" && isOwn(item);

  return (
    <div className="space-y-5" data-testid="my-submissions-page">
      <h1 className="text-xl font-heading font-bold text-navy">My Submissions</h1>

      <Tabs defaultValue="purchases" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-white border border-navy/10 h-12">
          <TabsTrigger value="purchases" className="flex-1 data-[state=active]:bg-navy data-[state=active]:text-cream text-sm min-h-[44px]" data-testid="tab-purchases">
            <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />Purchases ({purchases.length})
          </TabsTrigger>
          <TabsTrigger value="expenses" className="flex-1 data-[state=active]:bg-navy data-[state=active]:text-cream text-sm min-h-[44px]" data-testid="tab-expenses">
            <Receipt className="w-3.5 h-3.5 mr-1.5" />Expenses ({expenses.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="purchases" className="mt-3">
          {purchases.length === 0 ? <EmptyState text="No purchases recorded yet" /> : (
            <div className="space-y-2.5" data-testid="my-submissions-table">
              {purchases.map((p) => (
                <SubmissionCard key={p.id}
                  icon={ShoppingCart}
                  title={p.supplier_name || p.invoice_number || "Purchase"}
                  subtitle={`Invoice: ${p.invoice_number || "\u2014"}`}
                  amount={p.total_amount}
                  date={p.created_at}
                  status={p.status}
                  awaiting={p.duplicate_status === "pending_review"}
                  docs={p.document_ids?.length || 0}
                  onView={() => navigate(`/view-purchase/${p.id}`)}
                  onEdit={canEdit(p) ? () => navigate(`/edit-purchase/${p.id}`) : null}
                  onDelete={canDelete(p) ? () => setDeleteTarget({ type: "purchases", id: p.id, title: p.invoice_number || "this purchase" }) : null}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="expenses" className="mt-3">
          {expenses.length === 0 ? <EmptyState text="No expenses recorded yet" /> : (
            <div className="space-y-2.5" data-testid="my-submissions-table">
              {expenses.map((e) => (
                <SubmissionCard key={e.id}
                  icon={Receipt}
                  title={e.category || "Expense"}
                  subtitle={e.supplier_payee || e.description || "\u2014"}
                  amount={e.amount}
                  date={e.created_at}
                  status={e.status}
                  awaiting={e.exception_status === "pending"}
                  docs={e.document_ids?.length || 0}
                  onView={() => navigate(`/view-expense/${e.id}`)}
                  onEdit={canEdit(e) ? () => navigate(`/edit-expense/${e.id}`) : null}
                  onDelete={canDelete(e) ? () => setDeleteTarget({ type: "expenses", id: e.id, title: e.category || "this expense" }) : null}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard Draft</DialogTitle>
            <DialogDescription>
              Are you sure you want to discard &ldquo;{deleteTarget?.title}&rdquo;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="min-h-[44px]">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} className="min-h-[44px]" data-testid="confirm-delete-btn">Discard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubmissionCard({ icon: Icon, title, subtitle, amount, date, status, awaiting, docs, onView, onEdit, onDelete }) {
  const isDraft = status === "draft" || status === "rejected";

  return (
    <div className="bg-white rounded-xl border border-navy/5 overflow-hidden" data-testid="submission-card">
      {/* Clickable main area — opens detail view */}
      <button
        onClick={onView}
        className="w-full p-4 flex items-center gap-3 text-left active:bg-navy/[0.02] transition-colors"
        data-testid="view-transaction-btn"
        aria-label={`View ${title}`}
      >
        <div className="w-10 h-10 rounded-lg bg-navy/5 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-navy/40" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-navy truncate">{title}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_STYLES[status] || ""}`}>
              {status === "rejected" ? "returned" : status}
            </span>
            {awaiting && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap bg-amber-100 text-amber-700" data-testid="awaiting-manager-badge">awaiting manager</span>}
          </div>
          <p className="text-xs text-navy/40 truncate mt-0.5">{subtitle}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono text-sm font-bold text-navy">{formatGBP(amount)}</p>
          <p className="text-[10px] text-navy/35 mt-0.5">{formatDateUK(date)}</p>
          {docs > 0 && (
            <div className="flex items-center justify-end gap-0.5 mt-0.5">
              <FileText className="w-2.5 h-2.5 text-navy/25" />
              <span className="text-[10px] text-navy/30">{docs}</span>
            </div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-navy/20 shrink-0" />
      </button>

      {/* Action buttons — large, visible text labels, 44px+ touch targets */}
      {isDraft && (onEdit || onDelete) && (
        <div className="flex border-t border-navy/5">
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="flex-1 flex items-center justify-center gap-2 py-3 min-h-[44px] text-gold hover:bg-gold/5 active:bg-gold/10 transition-colors text-sm font-medium border-r border-navy/5"
              data-testid="edit-draft-btn"
              aria-label="Resume editing"
            >
              <Pencil className="w-4 h-4" />
              <span>Resume Editing</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="flex-1 flex items-center justify-center gap-2 py-3 min-h-[44px] text-red-500 hover:bg-red-50 active:bg-red-100 transition-colors text-sm font-medium"
              data-testid="delete-draft-btn"
              aria-label="Discard draft"
            >
              <Trash2 className="w-4 h-4" />
              <span>Discard</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center py-12">
      <FileText className="w-10 h-10 text-navy/15 mx-auto" />
      <p className="text-sm text-navy/35 mt-3">{text}</p>
    </div>
  );
}
